import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import ImageUploader from "../components/ImageUploader";
import { Megaphone, CreditCard, Coins, Eye, MousePointer, Clock, CheckCircle2, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  ACTIVE: "text-secondary border-secondary/40 bg-secondary/10",
  PENDING_PAYMENT: "text-primary border-primary/40 bg-primary/10",
  COMPLETED: "text-foreground/60 border-foreground/20 bg-foreground/5",
  REJECTED: "text-destructive border-destructive/40 bg-destructive/10",
};

export default function Ads() {
  const [params] = useSearchParams();
  const boostPostId = params.get("boost");
  const adPaid = params.get("ad_paid");

  const [ads, setAds] = useState([]);
  const [show, setShow] = useState(!!boostPostId);
  const [boostPost, setBoostPost] = useState(null);
  const [pricing, setPricing] = useState({ usdPerCaul: 0.01, minBudgetCaul: 100, minBudgetUsd: 1, impressionCaul: 1, clickCaul: 5 });
  const [walletBalance, setWalletBalance] = useState(0);
  const [form, setForm] = useState({
    title: "",
    description: "",
    mediaUrl: "",
    targetAudience: "",
    budget: 1000,
    durationDays: 7,
    paymentMethod: "caul",
  });

  const load = async () => {
    const { data } = await api.get("/ads"); setAds(data);
  };
  const loadPricing = async () => {
    try { const { data } = await api.get("/ads/pricing"); setPricing(data); } catch { /* noop */ }
  };
  const loadWallet = async () => {
    try { const { data } = await api.get("/wallet"); setWalletBalance(data?.balance ?? 0); } catch { /* noop */ }
  };
  const loadBoostPost = async (pid) => {
    try {
      const { data } = await api.get(`/posts`);
      const p = data.find((x) => x.id === pid);
      if (p) {
        setBoostPost(p);
        setForm((f) => ({
          ...f,
          title: `Boost: ${(p.content || "post").slice(0, 50)}`,
          description: p.content?.slice(0, 240) || "",
          mediaUrl: p.mediaUrl || "",
        }));
      }
    } catch { /* noop */ }
  };

  useEffect(() => { load(); loadPricing(); loadWallet(); }, []);
  useEffect(() => { if (boostPostId) loadBoostPost(boostPostId); /* eslint-disable-next-line */ }, [boostPostId]);
  useEffect(() => { if (adPaid) toast.success("Payment received — your ad is being activated."); /* eslint-disable-next-line */ }, [adPaid]);

  const usdAmount = useMemo(() => Math.max(0, Math.round((parseInt(form.budget || 0) || 0) * pricing.usdPerCaul * 100) / 100), [form.budget, pricing.usdPerCaul]);
  const estImpressions = useMemo(() => Math.floor((parseInt(form.budget || 0) || 0) / Math.max(1, pricing.impressionCaul)), [form.budget, pricing.impressionCaul]);
  const estClicks = useMemo(() => Math.floor((parseInt(form.budget || 0) || 0) / Math.max(1, pricing.clickCaul)), [form.budget, pricing.clickCaul]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    const budget = parseInt(form.budget);
    if (!budget || budget < pricing.minBudgetCaul) {
      toast.error(`Minimum budget is ${pricing.minBudgetCaul} CAUL ($${pricing.minBudgetUsd})`);
      return;
    }
    if (form.paymentMethod === "caul" && walletBalance < budget) {
      toast.error(`You only have ${walletBalance} CAUL — top up or switch to USD payment.`);
      return;
    }
    try {
      const payload = {
        title: form.title,
        description: form.description,
        mediaUrl: form.mediaUrl,
        targetAudience: form.targetAudience,
        budget,
        durationDays: parseInt(form.durationDays),
        paymentMethod: form.paymentMethod,
        postId: boostPostId || undefined,
        origin: window.location.origin,
      };
      const { data } = await api.post("/ads", payload);
      if (data.checkoutUrl) {
        toast.success("Redirecting to Stripe...");
        window.location.href = data.checkoutUrl;
        return;
      }
      toast.success("Campaign launched");
      setShow(false);
      setForm({ title: "", description: "", mediaUrl: "", targetAudience: "", budget: 1000, durationDays: 7, paymentMethod: "caul" });
      load(); loadWallet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mystic">Sponsored Reach</div>
            <h1 className="font-serif text-5xl mt-2">Cauldron Ads</h1>
            <p className="text-foreground/70 text-sm mt-2">Pay with <span className="text-secondary">CAUL</span> or <span className="text-primary">USD</span>. Boost any of your posts or run free-form copy.</p>
          </div>
          <button onClick={() => setShow(!show)} className="bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2" data-testid="ads-create-toggle">
            <Megaphone size={14} strokeWidth={2} /> {show ? "Close" : "+ New Campaign"}
          </button>
        </div>

        {show && (
          <div className="parchment p-6 space-y-4" data-testid="ad-create-form">
            {boostPost && (
              <div className="border border-primary/40 bg-primary/5 p-4 rounded-sm">
                <div className="label-mystic text-primary mb-1">Boosting Post</div>
                <p className="text-sm text-foreground/85 line-clamp-2">{boostPost.content}</p>
              </div>
            )}

            {/* Payment method toggle */}
            <div>
              <div className="label-mystic mb-2">Payment Method</div>
              <div className="grid grid-cols-2 gap-3" data-testid="ad-payment-toggle">
                <button type="button" onClick={() => setForm({ ...form, paymentMethod: "caul" })}
                  className={`p-4 border rounded-sm text-left transition-colors ${form.paymentMethod === "caul" ? "border-secondary bg-secondary/10" : "border-secondary/20 hover:bg-secondary/5"}`}
                  data-testid="ad-pay-caul">
                  <div className="flex items-center gap-2 mb-1"><Coins size={14} strokeWidth={1.5} className="text-secondary" /><span className="label-mystic text-secondary">CAUL Coin</span></div>
                  <div className="font-serif text-2xl">{walletBalance} CAUL</div>
                  <div className="text-xs text-foreground/60">Your balance</div>
                </button>
                <button type="button" onClick={() => setForm({ ...form, paymentMethod: "usd" })}
                  className={`p-4 border rounded-sm text-left transition-colors ${form.paymentMethod === "usd" ? "border-primary bg-primary/10" : "border-secondary/20 hover:bg-secondary/5"}`}
                  data-testid="ad-pay-usd">
                  <div className="flex items-center gap-2 mb-1"><CreditCard size={14} strokeWidth={1.5} className="text-primary" /><span className="label-mystic text-primary">USD (Stripe)</span></div>
                  <div className="font-serif text-2xl">${usdAmount.toFixed(2)}</div>
                  <div className="text-xs text-foreground/60">1 CAUL = ${pricing.usdPerCaul.toFixed(3)}</div>
                </button>
              </div>
            </div>

            <input placeholder="Campaign title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="ad-title" />
            <textarea placeholder="Describe the offer..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="ad-desc" />
            <ImageUploader value={form.mediaUrl} onChange={(v) => setForm({ ...form, mediaUrl: v })} testid="ad-media-upload" label="Upload Creative (optional)" />
            <input placeholder="Target keywords: Tarot, Hoodoo, Astrology..." value={form.targetAudience} onChange={(e) => setForm({ ...form, targetAudience: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="ad-audience" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mystic">Budget ({pricing.minBudgetCaul}+ CAUL)</label>
                <input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm mt-1" data-testid="ad-budget" />
              </div>
              <div>
                <label className="label-mystic">Duration (days)</label>
                <input type="number" value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: e.target.value })} className="w-full bg-input border border-secondary/20 p-3 rounded-sm mt-1" data-testid="ad-duration" />
              </div>
            </div>

            {/* Forecast */}
            <div className="grid grid-cols-3 gap-3 border-t border-secondary/15 pt-4" data-testid="ad-forecast">
              <Stat icon={Eye} label="Est. Impressions" value={estImpressions.toLocaleString()} />
              <Stat icon={MousePointer} label="Est. Clicks" value={estClicks.toLocaleString()} />
              <Stat icon={Clock} label="Days Run" value={form.durationDays} />
            </div>

            <button onClick={create} className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-sm" data-testid="ad-launch-btn">
              {form.paymentMethod === "usd" ? `Pay $${usdAmount.toFixed(2)} via Stripe` : `Launch for ${form.budget} CAUL`}
            </button>
          </div>
        )}

        <div className="space-y-3">
          {ads.length === 0 ? (
            <div className="parchment p-12 text-center text-foreground/50 italic font-serif text-xl">No campaigns yet. Light the first ember.</div>
          ) : ads.map((a) => (
            <AdRow key={a.id} ad={a} onChange={load} />
          ))}
        </div>
      </div>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <Icon size={16} strokeWidth={1.25} className="mx-auto text-secondary mb-1" />
      <div className="font-serif text-xl text-foreground/90">{value}</div>
      <div className="label-mystic">{label}</div>
    </div>
  );
}

function AdRow({ ad: a, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: a.title || "", description: a.description || "", targetUrl: a.targetUrl || "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/ads/${a.id}`, form);
      toast.success("Campaign updated");
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${a.title}"? Any unspent CAUL budget will be refunded to your wallet.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/ads/${a.id}`);
      toast.success(data.refundedCaul > 0 ? `Deleted · ${data.refundedCaul} CAUL refunded` : "Campaign deleted");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="parchment p-6 flex justify-between items-start flex-wrap gap-4" data-testid={`ad-${a.id}`}>
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`label-mystic px-2 py-0.5 border rounded-sm ${STATUS_COLORS[a.status] || ""}`}>{a.status}</span>
          {a.boostPostId && <span className="label-mystic text-primary">Boosted Post</span>}
          {a.paymentMethod && <span className="label-mystic text-foreground/50">{a.paymentMethod === "usd" ? "USD" : "CAUL"}</span>}
          {a.updatedAt && <span className="label-mystic text-foreground/40" data-testid={`ad-edited-${a.id}`}>· edited</span>}
        </div>
        {editing ? (
          <div className="space-y-2 mt-2">
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title"
              className="w-full bg-input border border-secondary/30 p-2 rounded-sm font-serif text-2xl"
              data-testid={`ad-edit-title-${a.id}`}
            />
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description"
              rows={3}
              className="w-full bg-input border border-secondary/30 p-2 rounded-sm text-sm"
              data-testid={`ad-edit-description-${a.id}`}
            />
            <input
              value={form.targetUrl}
              onChange={(e) => setForm({ ...form, targetUrl: e.target.value })}
              placeholder="Target URL (optional)"
              className="w-full bg-input border border-secondary/30 p-2 rounded-sm text-sm"
              data-testid={`ad-edit-target-${a.id}`}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setEditing(false); setForm({ title: a.title || "", description: a.description || "", targetUrl: a.targetUrl || "" }); }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm uppercase tracking-wider text-xs border border-secondary/30 text-foreground/80"
                data-testid={`ad-edit-cancel-${a.id}`}
              >
                <X size={12} strokeWidth={1.5} /> Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
                data-testid={`ad-edit-save-${a.id}`}
              >
                <Check size={12} strokeWidth={1.5} /> Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-serif text-2xl mt-1">{a.title}</h3>
            {a.description && <p className="text-foreground/75 text-sm mt-1 line-clamp-2">{a.description}</p>}
            <div className="flex items-center gap-4 mt-3 text-xs text-foreground/60">
              <span className="flex items-center gap-1"><Eye size={11} strokeWidth={1.5} /> {a.impressions || 0}</span>
              <span className="flex items-center gap-1"><MousePointer size={11} strokeWidth={1.5} /> {a.clicks || 0}</span>
              <span className="flex items-center gap-1"><CheckCircle2 size={11} strokeWidth={1.5} /> Spent {a.spent || 0} / {a.budget} CAUL</span>
            </div>
            <div className="mt-2 h-1.5 w-full bg-secondary/10 rounded-full overflow-hidden">
              <div className="h-full bg-secondary" style={{ width: `${Math.min(100, ((a.spent || 0) / a.budget) * 100)}%` }} />
            </div>
          </>
        )}
      </div>
      <div className="text-right flex flex-col items-end gap-2">
        <div>
          <div className="font-serif text-3xl text-secondary">{a.budget}</div>
          <div className="label-mystic">CAUL Budget</div>
        </div>
        {!editing && (
          <div className="flex gap-1.5 mt-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-2 rounded-sm border border-secondary/30 text-foreground/75 hover:bg-secondary/10 hover:text-foreground"
              title="Edit campaign"
              data-testid={`ad-edit-btn-${a.id}`}
              disabled={busy}
            >
              <Pencil size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={remove}
              className="p-2 rounded-sm border border-destructive/40 text-destructive hover:bg-destructive/10"
              title="Delete campaign"
              data-testid={`ad-delete-btn-${a.id}`}
              disabled={busy}
            >
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
